import { configApiRef } from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  FrontendPlugin,
  PageBlueprint,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
  oktaAuthApiRef,
} from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { OpsicleApiClient } from './api/OpsicleApiClient';
import { opsicleApiRef } from './api/apiRef';
import { rootRouteRef, bulkOperationsRouteRef } from './routes';

const opsicleApi = ApiBlueprint.make({
  name: 'opsicle',
  params: define =>
    define({
      api: opsicleApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        oktaAuthApi: oktaAuthApiRef,
      },
      factory: ({ discoveryApi, fetchApi, oktaAuthApi }) =>
        new OpsicleApiClient({ discoveryApi, fetchApi, oktaAuthApi }),
    }),
});

const opsiclePage = PageBlueprint.make({
  name: 'opsicle',
  params: {
    path: '/opsicle',
    routeRef: rootRouteRef,
    loader: () =>
      import('./components/OpsicleComponent').then(m => <m.OpsicleComponent />),
  },
});

const bulkOperationsPage = PageBlueprint.make({
  name: 'bulk-operations',
  params: {
    path: '/opsicle/bulk',
    routeRef: bulkOperationsRouteRef,
    loader: () =>
      import('./components/OpsicleComponent').then(m => (
        <m.BulkOperationsPage />
      )),
  },
});

const operationsEntityContent = EntityContentBlueprint.make({
  name: 'operations',
  params: {
    path: '/operations',
    title: 'Operations (Alpha)',
    filter: {
      $any: [
        { kind: 'component', 'spec.type': 'service' },
        { kind: 'component', 'spec.type': 'temporal-worker' },
        { kind: 'user' },
        { kind: 'group' },
      ],
    },
    loader: () => import('./components/Router').then(m => <m.Router />),
  },
});

const sreOperationsEntityContent = EntityContentBlueprint.make({
  name: 'sre-operations',
  params: {
    path: '/sre-operations',
    title: 'SRE Operations',
    filter: {
      $any: [
        { kind: 'component', 'spec.type': 'service' },
        { kind: 'component', 'spec.type': 'temporal-worker' },
        { kind: 'user' },
      ],
    },
    loader: () =>
      import('./components/OpsicleComponent').then(m => (
        <m.BulkOperationsPage />
      )),
  },
});

/**
 * Self-service replacement for InfraBot's Data Platform Health Service Slack
 * flow. Scoped to User and Group entities because it acts on a shard's
 * database fleet, not a catalog component.
 */
const dataPlatformOperationsEntityContent = EntityContentBlueprint.make({
  name: 'data-platform-operations',
  params: {
    path: '/data-platform-operations',
    title: 'Data Platform Operations',
    filter: {
      $any: [{ kind: 'user' }, { kind: 'group' }],
    },
    loader: () =>
      import('./components/DataPlatformOperations').then(m => (
        <m.DataPlatformOperationsPage />
      )),
  },
});

const sreDiagnosticsEntityContent = EntityContentBlueprint.make({
  name: 'sre-diagnostics',
  params: {
    path: '/sre-diagnostics',
    title: 'SRE Diagnostics',
    filter: {
      $any: [
        { kind: 'component', 'spec.type': 'service' },
        { kind: 'component', 'spec.type': 'temporal-worker' },
        { kind: 'user' },
        { kind: 'group' },
      ],
    },
    loader: () =>
      import('./components/SreDiagnosticsPage').then(m => (
        <m.SreDiagnosticsPage />
      )),
  },
});

const historyEntityContent = EntityContentBlueprint.make({
  name: 'operations-history',
  params: {
    path: '/operations-history',
    title: 'History',
    filter: {
      $any: [
        { kind: 'component', 'spec.type': 'service' },
        { kind: 'component', 'spec.type': 'temporal-worker' },
        { kind: 'user' },
        { kind: 'group' },
      ],
    },
    loader: () =>
      import('./components/HistoryPage').then(m => <m.HistoryPage />),
  },
});

const autoOpsConsoleEntityContent = EntityContentBlueprint.makeWithOverrides({
  name: 'auto-ops-console',
  factory(originalFactory, { apis }) {
    const configApi = apis.get(configApiRef);
    const enabled =
      configApi?.getOptionalBoolean('opsicle.poc.enabled') === true;
    return originalFactory({
      path: '/auto-ops-console',
      title: 'Auto Ops Console',
      filter: entity => {
        const kind = entity.kind.toLocaleLowerCase('en-US');
        return enabled && (kind === 'user' || kind === 'group');
      },
      loader: () =>
        import('./components/AutoOpsConsole').then(m => (
          <m.AutoOpsConsolePage />
        )),
    });
  },
});

const opsiclePlugin: FrontendPlugin = createFrontendPlugin({
  pluginId: 'opsicle',
  routes: { root: rootRouteRef, bulkOperations: bulkOperationsRouteRef },
  extensions: [
    opsicleApi,
    opsiclePage,
    bulkOperationsPage,
    operationsEntityContent,
    sreOperationsEntityContent,
    dataPlatformOperationsEntityContent,
    autoOpsConsoleEntityContent,
    sreDiagnosticsEntityContent,
    historyEntityContent,
  ],
});
export default opsiclePlugin;
